/**
 * FauxDS TextInput - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate TextInput component for prototyping.
 * It is NOT the real @faire/slate TextInput and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate TextInput, see: @faire/slate/components/text-input
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 7175-9212 (Slate Text Input)
 * - Design tokens extracted from Figma variables
 * - All states verified: Default, Active, Filled
 * - Error state verified
 * - Disabled state verified
 * - Label, helper text, and error text verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useId } from "react";

export interface TextInputProps {
  /**
   * The label of the text input
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current value (controlled)
   */
  value?: string;
  /**
   * **playground only**
   * Default value (uncontrolled). Slate TextInput is controlled via `value` only.
   */
  defaultValue?: string;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Error state - can be boolean or error message string
   */
  error?: boolean | string;
  /**
   * Helper text displayed below the input
   */
  helper?: string;
  /**
   * **playground only**
   * Input type. Slate TextInput does not expose `type` as a top-level prop.
   * @default "text"
   */
  type?: "text" | "email" | "password" | "search" | "tel" | "url" | "number";
  /**
   * **playground only**
   * Input name attribute. Slate passes this through underlying web props rather than a dedicated top-level prop.
   */
  name?: string;
  /**
   * **playground only**
   * Whether the input is required
   * @default false
   */
  required?: boolean;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * **playground only**
   * Accessible label (required if no label prop)
   */
  "aria-label"?: string;
}

/**
 * TextInput - FauxDS (recreated from Figma MCP)
 *
 * Text Inputs allow users to enter and edit text.
 * This implementation was created by extracting design specs directly from Figma.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      disabled = false,
      error,
      helper,
      type = "text",
      name,
      required = false,
      className = "",
      "aria-label": ariaLabel,
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = `textinput-figma-${generatedId}`;
    const helperId = `helper-figma-${generatedId}`;

    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : null;
    const helperText = errorMessage || helper;

    // Determine visual state styles based on Figma design tokens
    const getInputStyles = () => {
      if (disabled) {
        return {
          bg: "bg-[#f7f7f7]", // disabled surface
          border: "border-[#dfe0e1]", // disabled border
          text: "text-[#757575]", // disabled text
          placeholder: "placeholder:text-[#757575]", // disabled placeholder
        };
      }

      if (hasError) {
        return {
          bg: "bg-fs-surface-primary", // white surface
          border: "border-[#d17e70]", // critical border
          text: "text-fs-text-primary", // default text
          placeholder: "placeholder:text-[#757575]", // placeholder
          focusBorder: "focus:outline-[#d17e70]", // critical border
        };
      }

      return {
        bg: "bg-fs-surface-primary", // white surface
        border: "border-[#dfe0e1]", // subdued border
        text: "text-fs-text-primary", // default text
        placeholder: "placeholder:text-[#757575]", // placeholder
        focusBorder: "focus:outline-[#000000]", // active border
      };
    };

    const inputStyles = getInputStyles();

    // Label styles
    const getLabelStyles = () => {
      if (disabled) {
        return "text-[#757575]"; // disabled
      }
      if (hasError) {
        return "text-[#921100]"; // critical/error
      }
      return "text-fs-text-primary"; // default
    };

    const labelStyles = getLabelStyles();

    // Helper/Error text styles
    const getHelperStyles = () => {
      if (hasError) {
        return "text-[#921100]"; // critical/error
      }
      return "text-[#757575]"; // subdued (default and disabled)
    };

    const helperStyles = getHelperStyles();

    return (
      <div className={cn("flex flex-col", className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn("mb-[8px]", labelStyles)}
            style={{
              fontFamily: "var(--font-family-sans, 'Graphik', sans-serif)",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              letterSpacing: "0.15px",
            }}
          >
            {label}
            {required && <span className="ml-1 text-[#921100]">*</span>}
          </label>
        )}

        {/* Input */}
        <input
          ref={ref}
          id={inputId}
          type={type}
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-label={!label ? ariaLabel : undefined}
          aria-invalid={hasError}
          aria-describedby={helperText ? helperId : undefined}
          className={cn(
            "h-[40px] w-full rounded-[4px] border px-[16px] transition-all duration-150 ease-in-out",
            inputStyles.bg,
            inputStyles.border,
            inputStyles.text,
            inputStyles.placeholder,
            disabled ? "cursor-not-allowed" : "",
            "focus:outline focus:outline-1 focus:-outline-offset-1",
            !disabled && "focusBorder" in inputStyles
              ? inputStyles.focusBorder
              : "",
            !disabled ? "focus:text-[#000000]" : ""
          )}
          style={{
            fontFamily: "var(--font-family-sans, 'Graphik', sans-serif)",
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "20px",
            letterSpacing: "0.15px",
          }}
        />

        {/* Helper/Error Text */}
        {helperText && (
          <span
            id={helperId}
            className={cn("mt-[4px]", helperStyles)}
            style={{
              fontFamily: "var(--font-family-sans, 'Graphik', sans-serif)",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              letterSpacing: "0.15px",
            }}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
